import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import { setRestoreView } from "../../reducers/MapSlice";
import { getAlertsDashboard } from "../../api/FlaskAPI";
import { alertKeys } from "../../api/queryKeys";
import AccountMenu from "../AccountMenu";
import logo from "../../assets/bluebell-lockup.png";

const PAGES = [
  { path: "fields", Icon: GrassOutlinedIcon },
  { path: "owners", Icon: PersonOutlinedIcon },
  { path: "alerts", Icon: NotificationsOutlinedIcon },
  { path: "charts", Icon: InsightsOutlinedIcon },
];

const COLLAPSED_KEY = "dashboardSidebarCollapsed";
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

const Sidebar = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const projectName = useSelector((state) => state.auth?.projectName);
  const [collapsedSetting, setCollapsed] = useState(readCollapsed);
  const collapsed = !onClose && collapsedSetting;

  const alertParams = { status: "unreviewed", page: 1, per_page: 1 };
  const { data: alertData } = useQuery({
    queryKey: alertKeys.list(alertParams),
    queryFn: ({ signal }) =>
      getAlertsDashboard(alertParams, signal).then((res) => res.data),
  });
  const unreviewed = alertData?.counts?.unreviewed ?? 0;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(!prev));
      } catch {
        // for sidebar to work if localStorage is unavailable.
      }
      return !prev;
    });
  };

  const handleBackToMap = () => {
    dispatch(setRestoreView(true));
    navigate("/");
  };

  // Collapsed, labels become tooltips.
  const withTip = (title, child) =>
    collapsed ? (
      <Tooltip title={title} placement="right">
        {child}
      </Tooltip>
    ) : (
      child
    );

  return (
    <Box
      component="aside"
      sx={{
        width: collapsed ? 72 : 248,
        flexShrink: 0,
        height: onClose ? "100%" : "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRight: "1px solid #e5e7eb",
        transition: "width 0.2s ease",
        overflow: "hidden",
      }}
    >
      {/* Logo and collapse */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          height: 64,
        }}
      >
        {!collapsed && (
          <Box component="img" src={logo} alt="Bluebell" sx={{ height: 28 }} />
        )}
        {onClose ? (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("dashboard.closeMenu")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <Tooltip
            title={
              collapsed
                ? t("dashboard.expandSidebar")
                : t("dashboard.collapseSidebar")
            }
            placement="right"
          >
            <IconButton
              size="small"
              onClick={toggleCollapsed}
              aria-label={
                collapsed
                  ? t("dashboard.expandSidebar")
                  : t("dashboard.collapseSidebar")
              }
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <KeyboardDoubleArrowRightIcon
                  className="flip-rtl"
                  fontSize="small"
                />
              ) : (
                <KeyboardDoubleArrowLeftIcon
                  className="flip-rtl"
                  fontSize="small"
                />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Back to map */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 2 }}>
        {withTip(
          t("dashboard.backToMap"),
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToMap}
            startIcon={collapsed ? null : <MapOutlinedIcon />}
            sx={{
              minWidth: 0,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              py: 0.75,
            }}
          >
            {collapsed ? (
              <MapOutlinedIcon fontSize="small" />
            ) : (
              t("dashboard.backToMap")
            )}
          </Button>,
        )}
      </Box>

      {/* Pages */}
      <Box component="nav" sx={{ px: collapsed ? 1 : 1.5, flex: 1 }}>
        {PAGES.map(({ path, Icon }) => {
          const label = t(`dashboard.nav.${path}`);
          const badge = path === "alerts" && unreviewed > 0 ? unreviewed : null;
          return (
            <Box key={path}>
              {withTip(
                badge
                  ? t("dashboard.unreviewedTip", { label, count: badge })
                  : label,
                <Box
                  component={NavLink}
                  to={`/dashboard/${path}`}
                  onClick={onClose}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 1.5,
                    px: collapsed ? 0 : 1.5,
                    py: 1,
                    mb: 0.5,
                    borderRadius: "10px",
                    color: "#4b5563",
                    fontSize: 14,
                    fontWeight: 500,
                    textDecoration: "none",
                    // Global link styles (rsuite) underline on hover.
                    "&:hover, &:focus": { textDecoration: "none" },
                    "&:hover": { bgcolor: "#f3f4f6", color: "#111827" },
                    "&.active": {
                      bgcolor: "rgba(var(--primary-rgb), 0.1)",
                      color: "primary.main",
                      fontWeight: 600,
                    },
                    // A short bar for current page.
                    "&.active::before": {
                      content: '""',
                      position: "absolute",
                      left: collapsed ? -8 : -12,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: 2,
                      bgcolor: "primary.main",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                  {!collapsed && <Box sx={{ flex: 1 }}>{label}</Box>}
                  {badge && (
                    <Box
                      component="span"
                      sx={{
                        // Collapsed: a small badge on the icon's corner.
                        position: collapsed ? "absolute" : "static",
                        top: 2,
                        left: "calc(50% + 2px)",
                        minWidth: collapsed ? 16 : 20,
                        height: collapsed ? 16 : 20,
                        px: collapsed ? 0.5 : 0.75,
                        borderRadius: "999px",
                        bgcolor: "primary.main",
                        color: "#fff",
                        fontSize: collapsed ? 9 : 11,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {badge > 99 ? "99+" : badge}
                    </Box>
                  )}
                </Box>,
              )}
            </Box>
          );
        })}
      </Box>

      {/* Project and account */}
      <Box sx={{ p: collapsed ? 1 : 1.5, borderTop: "1px solid #e5e7eb" }}>
        {withTip(
          t("dashboard.projectTip", { name: projectName || "—" }),
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 1.25,
              p: 1,
              mb: 0.5,
              borderRadius: "10px",
              bgcolor: "#f5f7fa",
            }}
          >
            <FolderOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  {t("dashboard.project")}
                </Typography>
                <Typography
                  noWrap
                  title={projectName}
                  sx={{ fontSize: 14, fontWeight: 600 }}
                >
                  {projectName || "—"}
                </Typography>
              </Box>
            )}
          </Box>,
        )}
        <AccountMenu variant={collapsed ? "avatar" : "row"} />
      </Box>
    </Box>
  );
};

export default Sidebar;
